import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TEMPLATE_CONSTANT } from '@app/constants/template.constants';
import { CalioMeetingTemplatePipe } from '@app/pipes/calio-meeting-template.pipe';
import { CalioTranslationPipe } from '@app/pipes/calio-translation.pipe';
import { CouponDbModel } from '@models/coupon-db.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BookingService } from '@services/feature/booking.service';
import { CustomEventService } from '@services/feature/custom-event.service';
import { WidgetService } from '@services/feature/widget.service';
import { LoggerService } from '@services/utils/logger.service';

@Component({
  selector: 'app-coupon-form',
  templateUrl: './coupon-form.component.html',
  styleUrls: ['./coupon-form.component.scss'],
  imports: [FormsModule, TranslateModule, CalioMeetingTemplatePipe, CalioTranslationPipe]
})
export class CouponFormComponent implements OnInit {

  private customEventService = inject(CustomEventService);
  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);
  private widgetService = inject(WidgetService);
  protected bookingService = inject(BookingService);

  protected readonly templateConstant = TEMPLATE_CONSTANT;

  protected coupon: string;
  protected showError: boolean;
  protected showSuccessMsg: boolean;
  protected showInsufficientBalanceError: boolean;
  protected showEmailRequiredError: boolean;
  protected remainingBalance = 0;
  protected showRemainingBalanceNote = false;
  protected lang: string;

  constructor() {
    this.lang = this.translate.getFallbackLang();
    this.translate.onLangChange.subscribe(language => this.lang = language.lang);
  }

  ngOnInit(): void {
    this.customEventService.removeCouponEvent.subscribe(() => {
      this.showError = false;
      this.showSuccessMsg = false;
      this.coupon = undefined;
    });

    if (this.widgetService.widgetConf?.context?.coupon) {
      this.coupon = this.widgetService.widgetConf?.context?.coupon;
      this.onApplyCoupon();
    }
  }

  onApplyCoupon(): void {
    this.showError = false;
    this.showSuccessMsg = false;
    this.showInsufficientBalanceError = false;
    this.showEmailRequiredError = false;
    this.customEventService.couponFailedEvent.emit({
      disableBookingButton: false,
      invalidCoupon: true,
    });
    // this.couponFailedEvent.emit();
    if (!this.coupon || this.coupon.trim() === '') {
      // this.customEventService.resetPayPalOrderDetailEvent.emit();
      return;
    }
    const couponRequest = {
      coupon: this.coupon,
      ...this.bookingService.selectedBookingData?.selectedUser.eMail && { email: this.bookingService.selectedBookingData.selectedUser.eMail },
      ... this.bookingService.selectedBookingData.subTotal && { amount: this.bookingService.selectedBookingData.subTotal },
    }
    this.bookingService.applyCoupon(couponRequest)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: CouponDbModel) => {
          this.showSuccessMsg = true;
          if (result.coupon_type_id === 7) {
            this.showRemainingBalanceNote = true;
            this.remainingBalance = +result.balance;
          }
          this.customEventService.couponSuccessEvent.emit(result);
        },
        error: (error: HttpErrorResponse) => {
          LoggerService.error(error);
          if (error.error.errors && error.error.errors.length === 1) {
            if (error.error.errors[0].code === HttpStatusCode.NotFound) {
              this.showError = true;
              this.customEventService.couponFailedEvent.emit({
                disableBookingButton: true, invalidCoupon: true
              });
              // this.customEventService.resetPayPalOrderDetailEvent.emit();
            } else if (error.error.errors[0].code === HttpStatusCode.BadRequest) {
              this.showError = true;
              this.customEventService.couponFailedEvent.emit({
                disableBookingButton: true, invalidCoupon: true
              });
              // this.customEventService.resetPayPalOrderDetailEvent.emit();
            } else if (error.error.errors[0].code === HttpStatusCode.Unauthorized) {
              this.showError = true;
              this.customEventService.couponFailedEvent.emit({
                disableBookingButton: true, invalidCoupon: true
              });
              // this.customEventService.resetPayPalOrderDetailEvent.emit();
            } else if (error.error.errors[0].code === HttpStatusCode.Forbidden) {
              this.showError = true;
              this.customEventService.couponFailedEvent.emit({
                disableBookingButton: true, invalidCoupon: true
              });
              // this.customEventService.resetPayPalOrderDetailEvent.emit();
            } else if (error.error.errors[0].code === HttpStatusCode.NotAcceptable) {
              this.showEmailRequiredError = true;
              this.customEventService.couponFailedEvent.emit({
                disableBookingButton: true, invalidCoupon: true
              });
              // this.customEventService.resetPayPalOrderDetailEvent.emit();
            } else if (error.error.errors[0].code === HttpStatusCode.Conflict) {
              this.showInsufficientBalanceError = true;
              this.customEventService.couponFailedEvent.emit({
                disableBookingButton: true, invalidCoupon: true
              });
              // this.customEventService.resetPayPalOrderDetailEvent.emit();
            }
          }
        }
      });
  }
}
